import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import PhysicianUtils from 'services/PhysicianUtils';
import { Shift, Physician, PatientHandover } from 'generated/api';

const BREAK_REASONS = {
  lunch: 'Lunch',
  admin_time: 'Admin Time',
} as const;

const COLOR_VACATION = 'darkgrey';
const COLORS = [
  '#d32f2f',
  '#d8417f',
  '#9c27b0',
  '#3f51b5',
  '#039be5',
  '#00a597',
  '#158523',
  '#ffb300',
  '#795548',
  '#ff5722',
  '#9c786c',
  '#607d8b',
  '#1a237e',
  '#009688',
  '#5d4037',
  '#673ab7',
  '#3d5afe',
  '#00c853',
  '#ffd600',
  '#6d4c41',
];

const chosenColors: Record<string, string> = {};
const chosenUserColors: Record<string, string> = {};

interface ShiftEvent {
  type: string;
  className: string;
  text: string;
  shift: Shift;
  start: Date;
  end: Date;
}

interface ShiftTemplate {
  start_hour: string;
  end_hour: string;
  breaks?: any[];
  non_working?: boolean;
  non_working_reason?: string;
  approved?: boolean;
  telemedicine_only?: boolean;
}

class ShiftUtils {
  shiftsToEvents(
    shifts: Shift[] | undefined,
    physicians: Record<string, Physician>,
    handovers: PatientHandover[],
  ): ShiftEvent[] {
    if (!shifts) return [];

    return shifts.map((shift) => {
      let startDate = moment.unix(shift.start);
      let endDate = moment.unix(shift.end);

      let startString = CalendarUtils.format(shift.start, 'h:mmA');
      let endString = CalendarUtils.format(shift.end, 'h:mmA');

      let physicianName = PhysicianUtils.getName(physicians[shift.user_id!]);

      let className = 'shift';

      let text = `${physicianName}: ${startString} - ${endString}`;
      if (shift.telemedicine_only) {
        text += ' (Telemedicine Only)';
      }
      if (shift.non_working) {
        const REASONS = this.getBreakReasons();

        let reason = Object.values(REASONS).includes(shift.non_working_reason as any)
          ? shift.non_working_reason
          : `Non-working`;

        if (reason !== 'Non-working') {
          text = `${reason} ${startString}`;
        } else {
          const handover = this.getActiveHandover(handovers, shift.user_id!, shift.start);
          const coveredByPhysician = physicians[handover?.assigned_to_id!];
          let coveredBy = coveredByPhysician ? PhysicianUtils.getName(coveredByPhysician) : 'none';
          text = `${reason} ${startString} - ${endString} Covered by ${coveredBy}`;
        }

        className += ' non-working';

        if (shift.non_working_reason === REASONS.admin_time) {
          className += ' admin-time';
        }
      }

      return {
        type: 'shift',
        className,
        text,
        shift,
        start: startDate.toDate(),
        end: endDate.toDate(),
      };
    });
  }

  getBreakReasons(): typeof BREAK_REASONS {
    return BREAK_REASONS;
  }

  getShiftDescription(shift: Partial<Shift> = {}): string {
    let { start, end, non_working, non_working_reason, half_day } = shift;

    if (!start) return 'Custom Shift';

    if (non_working && non_working_reason) {
      let description = non_working_reason;
      if (non_working_reason === 'Vacation') {
        description = 'Paid Time Off';
      }
      if (half_day) description += ` (${half_day === '1' ? '1st half off' : '2nd half off'} )`;
      return description;
    }

    let format = 'hA';
    if (moment.unix(start).minutes() !== 0) format = 'h:mmA';

    let startLabel = CalendarUtils.format(start, format);

    format = 'hA';
    if (moment.unix(end!).minutes() !== 0) format = 'h:mmA';

    let endLabel = CalendarUtils.format(end!, format);
    return `${startLabel}-${endLabel}`;
  }

  resetColors(): void {
    Object.keys(chosenColors).forEach((key) => {
      delete chosenColors[key];
    });
    Object.keys(chosenUserColors).forEach((key) => {
      delete chosenUserColors[key];
    });
  }

  getShiftHash(shift: Partial<Shift> = {}): string {
    const { start, end, breaks = [], non_working, telemedicine_only } = shift;

    const startDate = start ? CalendarUtils.getMilitaryTime(start) : undefined;
    const endDate = end ? CalendarUtils.getMilitaryTime(end) : undefined;
    const breakString = (breaks as any[])
      .map((data: any) => data.name + data.start_hour + data.end_hour)
      .join(',');

    let hash = '';
    hash += `start=${startDate}`;
    hash += `end=${endDate}`;
    hash += `breaks=${breakString}`;
    hash += `telemedicine_only=${telemedicine_only}`;

    if (non_working) hash = 'non_working';

    return hash;
  }

  getBreakDescription(data: any = {}): string {
    return `${data.name}: ${CalendarUtils.formatMilitaryTime(
      data.start_hour,
    )} - ${CalendarUtils.formatMilitaryTime(data.end_hour)}`;
  }

  getColor(shift: Partial<Shift>, withUser: boolean = false): string {
    let hash: string = this.getShiftHash(shift);
    let colors = chosenColors;
    if (shift.non_working) return COLOR_VACATION;
    if (withUser) {
      hash = shift.user_id!;
      colors = chosenUserColors;
    }
    if (!colors[hash]) {
      colors[hash] = COLORS[Object.keys(colors).length % COLORS.length]!;
    }
    return colors[hash]!;
  }

  shiftToTemplate(shift: Shift): ShiftTemplate {
    return {
      start_hour: CalendarUtils.getMilitaryTime(shift.start, CalendarUtils.getTargetTimezone()),
      end_hour: CalendarUtils.getMilitaryTime(shift.end, CalendarUtils.getTargetTimezone()),
      breaks: shift.breaks as any,
      non_working: shift.non_working,
      non_working_reason: shift.non_working_reason,
    };
  }

  templateToShift(
    date: moment.Moment = moment(),
    template: ShiftTemplate,
    user: Partial<Physician> = {},
    clinicId?: string,
  ): Partial<Shift> {
    let start: number | undefined, end: number | undefined;

    if (!template.start_hour) {
      start = CalendarUtils.militaryTimeToUnix(date.unix(), '0800');
    }

    if (template.start_hour && template.end_hour) {
      start = CalendarUtils.militaryTimeToUnix(date.unix(), template.start_hour);
      end = CalendarUtils.militaryTimeToUnix(date.unix(), template.end_hour);
    }

    return {
      start,
      end,
      non_working: template.non_working,
      non_working_reason: template.non_working_reason,
      clinic_id: clinicId || user.clinic_id,
      breaks: template.breaks as any,
      approved: template.approved,
      user_id: user.id,
      telemedicine_only: template.telemedicine_only,
    };
  }

  getActiveHandover(
    handovers: PatientHandover[],
    creatorId: string,
    date: number,
  ): PatientHandover | undefined {
    return handovers
      .filter((handover) => this.isRelevantPatientHandover(handover, creatorId, false, date))
      .find(({ physician_id }) => physician_id === creatorId);
  }

  getRelevantHandovers(
    handovers: PatientHandover[],
    ownId: string,
    ignoreEnd?: boolean,
  ): PatientHandover[] {
    return handovers.filter((handover) =>
      this.isRelevantPatientHandover(handover, ownId, ignoreEnd),
    );
  }

  isRelevantPatientHandover(
    handover: PatientHandover,
    ownId: string | undefined,
    ignoreEnd: boolean = false,
    date: number = moment().unix(),
  ): boolean {
    let isArchived = handover.archived === true;
    if (isArchived) return false;

    let dateMoment = moment.unix(date);
    let dateStart = moment.unix(handover.date_start!).startOf('day');
    let dateEnd = moment.unix(handover.date_end!).endOf('day');
    let isDateOk = dateMoment.isBetween(dateStart, dateEnd);

    if (ignoreEnd) {
      isDateOk = dateMoment.isAfter(dateStart);
    }

    if (!isDateOk) return false;

    let isAuthor = !ownId || handover.physician_id === ownId;
    let isAssignedTo = !ownId || handover.assigned_to_id === ownId;
    if (isAuthor || isAssignedTo) return true;
    return !!(handover as any)?.patients?.find((info: any) => info.assigned_to_id === ownId);
  }
}

export default new ShiftUtils();
